import { Add, UnfoldMore } from '@mui/icons-material'
import {
  Box,
  Button,
  ButtonBase,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { ReactNode } from 'react'
import { useProduct } from '@/context/product'
import { AccountingIcon, CreditIcon } from './product-icons'

function Bubble({
  icon,
  dashed = false,
  front = false,
}: {
  icon: ReactNode
  dashed?: boolean
  front?: boolean
}) {
  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        top: 0,
        left: front ? 0 : '18px',
        zIndex: front ? 2 : 1,
        width: 34,
        height: 34,
        borderRadius: '50%',
        display: 'grid',
        placeItems: 'center',
        fontSize: 19,
        color: dashed
          ? 'primary.light'
          : front
            ? 'secondary.main'
            : 'primary.main',
        bgcolor: dashed
          ? 'background.paper'
          : front
            ? 'primary.main'
            : 'primary.100',
        border: dashed ? '1.5px dashed' : 'none',
        borderColor: dashed ? 'primary.light' : undefined,
        boxShadow: front
          ? (theme) => `0 0 0 2px ${theme.palette.background.paper}`
          : undefined,
      }}
    >
      {icon}
    </Box>
  )
}

export function ProductSwitcherButton() {
  const {
    product,
    companies,
    hasAccounting,
    hasCredit,
    accountingAvailable,
    creditAvailable,
    creditHasPending,
    selectedCompanyKey,
    tipOpen,
    openSheet,
    dismissTip,
  } = useProduct()

  const currentCompany = companies.find(
    (company) => company.company_key === selectedCompanyKey,
  )
  const isCredit = product === 'credit'
  const ownsOther = isCredit ? hasAccounting : hasCredit
  const otherAvailable = isCredit ? accountingAvailable : creditAvailable
  const otherHasPending = isCredit
    ? companies.some((company) => company.has_pending)
    : creditHasPending
  const currentIcon = isCredit ? <CreditIcon /> : <AccountingIcon />
  const otherIcon = isCredit ? <AccountingIcon /> : <CreditIcon />
  const title = isCredit ? 'Crédito' : 'Contabilidade'
  const subtitle = isCredit
    ? 'Pessoa física'
    : currentCompany?.corporate_name || 'Sua empresa'
  const otherName = isCredit ? 'Contabilidade' : 'Crédito'
  const tip = ownsOther ? (
    <>
      <b>Seus dois produtos num lugar só.</b> Toque aqui para ir{' '}
      {isCredit ? 'à' : 'ao'} {otherName}.
    </>
  ) : (
    <>
      <b>Novo:</b> conheça {isCredit ? 'a' : 'o'} {otherName} da Alume. Toque
      aqui para ver.
    </>
  )

  return (
    <Tooltip
      open={tipOpen && otherAvailable}
      placement="bottom-start"
      arrow
      slotProps={{
        tooltip: {
          sx: {
            width: 260,
            maxWidth: 260,
            bgcolor: 'primary.dark',
            color: 'common.white',
            borderRadius: '12px',
            px: '14px',
            py: '12px',
            boxShadow: 8,
          },
        },
        arrow: {
          sx: {
            color: 'primary.dark',
            transform: 'translate3d(22px, 0, 0) !important',
          },
        },
      }}
      title={
        <Stack spacing={1}>
          <Typography
            sx={{
              fontSize: 13.5,
              lineHeight: 1.45,
              color: 'inherit',
              '& b': { fontWeight: 600 },
            }}
          >
            {tip}
          </Typography>
          <Button
            size="small"
            variant="text"
            onClick={dismissTip}
            sx={{
              alignSelf: 'flex-end',
              minWidth: 0,
              px: 0.5,
              py: 0.25,
              color: 'secondary.main',
              fontWeight: 500,
              fontSize: 13.5,
              textTransform: 'none',
              '&:hover': {
                bgcolor: 'transparent',
                textDecoration: 'underline',
              },
            }}
          >
            Entendi
          </Button>
        </Stack>
      }
    >
      <ButtonBase
        onClick={openSheet}
        aria-haspopup="dialog"
        aria-label={`Trocar de produto. Atual: ${title}`}
        sx={{
          display: 'grid',
          gridTemplateColumns: '50px minmax(0, 1fr) 24px',
          gap: '12px',
          alignItems: 'center',
          maxWidth: 280,
          py: '10px',
          px: '12px',
          borderRadius: '12px',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          color: 'text.primary',
          textAlign: 'left',
          '&:hover': { bgcolor: 'primary.50' },
        }}
      >
        <Box sx={{ position: 'relative', width: 50, height: 34 }}>
          {otherAvailable && <Bubble icon={otherIcon} dashed={!ownsOther} />}
          <Bubble icon={currentIcon} front />
          {otherAvailable && !ownsOther && (
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                bottom: -2,
                right: -3,
                zIndex: 3,
                width: 15,
                height: 15,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                bgcolor: 'secondary.main',
                color: 'primary.dark',
                boxShadow: (theme) =>
                  `0 0 0 2px ${theme.palette.background.paper}`,
              }}
            >
              <Add sx={{ fontSize: 12 }} />
            </Box>
          )}
          {otherAvailable && ownsOther && otherHasPending && (
            <Box
              aria-label="Pendência"
              sx={{
                position: 'absolute',
                top: -1,
                right: -2,
                zIndex: 3,
                width: 11,
                height: 11,
                borderRadius: '50%',
                bgcolor: 'error.main',
                boxShadow: (theme) =>
                  `0 0 0 2px ${theme.palette.background.paper}`,
              }}
            />
          )}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            component="span"
            noWrap
            sx={{
              display: 'block',
              fontSize: 16,
              fontWeight: 500,
              lineHeight: 1.2,
            }}
          >
            {title}
          </Typography>
          <Typography
            component="span"
            noWrap
            sx={{
              display: 'block',
              fontSize: 12,
              color: 'text.secondary',
              lineHeight: 1.2,
            }}
          >
            {subtitle}
          </Typography>
        </Box>
        <UnfoldMore sx={{ fontSize: 24, color: 'text.secondary' }} />
      </ButtonBase>
    </Tooltip>
  )
}
